import React from 'react';
import { Calendar, ClipboardList, ExternalLink, Clock, CheckCircle2, XCircle, AlertCircle, Trash2 } from 'lucide-react';
import { Trial } from '../../services/trialService';

interface TrialSubmissionCardProps {
    trial: Trial;
    onClick?: () => void;
    onCancel?: (e: React.MouseEvent) => void;
}

const statusConfig = {
    PENDING: {
        label: '待处理',
        color: 'text-amber-600 bg-amber-50 border-amber-100',
        icon: Clock
    },
    ACCEPTED: {
        label: '待交付',
        color: 'text-blue-600 bg-blue-50 border-blue-100',
        icon: AlertCircle
    },
    REJECTED: {
        label: '已拒绝',
        color: 'text-gray-500 bg-gray-50 border-gray-100',
        icon: XCircle
    },
    SUBMITTED: {
        label: '审核中',
        color: 'text-purple-600 bg-purple-50 border-purple-100',
        icon: Clock
    },
    PASSED: {
        label: '已通过',
        color: 'text-green-600 bg-green-50 border-green-100',
        icon: CheckCircle2
    },
    FAILED: {
        label: '未通过',
        color: 'text-red-600 bg-red-50 border-red-100',
        icon: XCircle
    }
};

export const TrialSubmissionCard: React.FC<TrialSubmissionCardProps> = ({ trial, onClick, onCancel }) => {
    const config = statusConfig[trial.status];
    const StatusIcon = config.icon;

    return (
        <div
            onClick={onClick}
            className="group relative bg-white rounded-3xl border border-gray-100 p-6 transition-all duration-300 hover:shadow-xl hover:border-gray-200 cursor-pointer"
        >
            <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <ClipboardList className="w-4 h-4 text-gray-400" />
                        <h3 className="text-lg font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                            {trial.briefTitle}
                        </h3>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                        <div className="flex items-center gap-1.5">
                            <img src={trial.clientAvatar} alt={trial.clientName} className="w-5 h-5 rounded-full" />
                            <span>{trial.clientName}</span>
                        </div>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>{new Date(trial.createdAt).toLocaleDateString('zh-CN')}</span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {onCancel && (
                        <button 
                            onClick={onCancel}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                            title="取消试稿"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    )}
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold ${config.color}`}>
                        <StatusIcon className="w-3.5 h-3.5" />
                        {config.label}
                    </div>
                </div>
            </div>

            <div className="space-y-3">
                <div className="flex items-center justify-between text-sm py-2 px-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-2">
                        <span className="text-gray-500">预算范围</span>
                        <span className="font-semibold text-gray-900">{trial.budgetRange}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-gray-500">费用</span>
                        <span className="font-semibold text-gray-900">
                            {trial.trialFeeAmount && trial.trialFeeAmount > 0 ? `¥${trial.trialFeeAmount.toFixed(2)}` : '免费'}
                        </span>
                    </div>
                </div>

                {trial.notes && (
                    <p className="text-sm text-gray-600 line-clamp-2 bg-slate-50/50 p-3 rounded-xl border border-dashed border-gray-200">
                        <span className="font-medium text-gray-900">备注：</span>{trial.notes}
                    </p>
                )}

                {trial.feedback && (
                    <div className={`p-4 rounded-2xl text-sm ${trial.status === 'PASSED' ? 'bg-green-50/50 text-green-700 border border-green-100' : 'bg-red-50/50 text-red-700 border border-red-100'}`}>
                        <div className="font-bold flex items-center gap-1.5 mb-1">
                            {trial.status === 'PASSED' ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                            评价反馈
                        </div>
                        {trial.feedback}
                    </div>
                )}
            </div>

            <div className="mt-5 pt-4 border-t border-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                    ID: {trial.id}
                </div>
                <div className="flex items-center gap-1 text-sm font-medium text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                    查看详情 <ExternalLink className="w-3.5 h-3.5" />
                </div>
            </div>
        </div>
    );
};
